<template>
  <div class="kpi-card" :data-aos="aos">
    <div class="shimmer-overlay"></div>
    <div class="decor-cut top-left"></div>
    <div class="decor-cut bottom-right"></div>
    <div class="glow-orb"></div>

    <div class="content">
      <div class="icon-area">
        <div class="icon-bg-pulse"></div>
        <i :class="`mdi ${mdiIcon}`"></i>
      </div>

      <div class="text-section">
        <div class="stat-value">
          <span class="number-animate">{{ valueText }}</span>
          <span class="plus-sign">+</span>
        </div>
        <div class="stat-label">{{ label }}</div>
      </div>
    </div>
    <div class="floating-particles">
      <span class="particle" v-for="n in 5" :key="n" :style="particleStyle(n)"></span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KpiCard',
  props: {
    label: { type: String, required: true },
    valueText: { type: String, required: true },
    progress: { type: Number, required: true },
    iconType: {
      type: String,
      required: true,
      validator: (v) => ['download', 'user', 'partner'].includes(v),
    },
    aos: { type: String, default: 'fade-up' },
  },
  computed: {
    mdiIcon() {
      switch (this.iconType) {
        case 'download':
          return 'mdi-download-outline';
        case 'user':
          return 'mdi-account-group-outline';
        case 'partner':
          return 'mdi-handshake-outline';
        default:
          return 'mdi-chart-line';
      }
    },
  },
  methods: {
    particleStyle(n) {
      return {
        '--delay': `${n * 0.8}s`,
        '--x': `${Math.random() * 100}%`,
        '--y': `${Math.random() * 100}%`,
      };
    },
  },
};
</script>

<style scoped>
@import url('https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css');
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');

@keyframes iconPopIn {
  0% {
    transform: scale(0.5) rotate(-10deg);
    opacity: 0;
  }
  60% {
    transform: scale(1.1) rotate(5deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.5);
    opacity: 0;
  }
}

@keyframes glowOrb {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.15;
  }
  50% {
    transform: translate(-30%, -60%) scale(1.3);
    opacity: 0.25;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translate(var(--x), var(--y)) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translate(calc(var(--x) + 20px), calc(var(--y) - 50px)) scale(1);
    opacity: 0;
  }
}

@keyframes fillShimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

.kpi-card {
  position: relative;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%);
  border-radius: 1.5rem;
  backdrop-filter: blur(20px) saturate(180%);
  padding: 2rem;
  overflow: hidden;
  border: 1px solid rgba(244, 101, 35, 0.12);
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.04),
    0 12px 40px rgba(244, 101, 35, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  cursor: pointer;
  isolation: isolate;
}

.kpi-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 1.5rem;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(244, 101, 35, 0.3),
    transparent 50%,
    rgba(244, 101, 35, 0.1)
  );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.kpi-card:hover {
  transform: translateY(-12px) scale(1.02);
  box-shadow:
    0 8px 30px rgba(0, 0, 0, 0.06),
    0 20px 60px rgba(244, 101, 35, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 1);
  border-color: rgba(244, 101, 35, 0.3);
}

.kpi-card:hover::before {
  opacity: 1;
}

.shimmer-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    110deg,
    transparent 20%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 80%
  );
  transform: translateX(-100%) translateY(-100%) rotate(45deg);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.kpi-card:hover .shimmer-overlay {
  animation: shimmer 1.5s ease-in-out;
  opacity: 1;
}

.glow-orb {
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(244, 101, 35, 0.3) 0%, transparent 70%);
  top: -50%;
  right: -20%;
  pointer-events: none;
  filter: blur(50px);
  animation: glowOrb 6s ease-in-out infinite;
  z-index: 0;
}

.decor-cut {
  position: absolute;
  width: 100px;
  height: 100px;
  z-index: 1;
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  opacity: 0.8;
}

.top-left {
  top: 0;
  left: 0;
  clip-path: polygon(0 0, 100% 0, 0 100%);
  background: linear-gradient(135deg, rgba(244, 101, 35, 0.12), transparent);
}

.bottom-right {
  bottom: 0;
  right: 0;
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
  background: linear-gradient(315deg, rgba(244, 101, 35, 0.12), transparent);
}

.kpi-card:hover .top-left {
  transform: translate(-15px, -15px) scale(1.1);
  background: linear-gradient(135deg, rgba(244, 101, 35, 0.25), transparent);
}

.kpi-card:hover .bottom-right {
  transform: translate(15px, 15px) scale(1.1);
  background: linear-gradient(315deg, rgba(244, 101, 35, 0.25), transparent);
}

.content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  z-index: 2;
}

.icon-area {
  position: relative;
  background: linear-gradient(135deg, #f46523 0%, #ff6b3d 50%, #ff8c5a 100%);
  color: white;
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  flex-shrink: 0;
  box-shadow:
    0 8px 24px rgba(244, 101, 35, 0.35),
    inset 0 -2px 8px rgba(0, 0, 0, 0.15),
    inset 0 2px 4px rgba(255, 255, 255, 0.3);
  animation: iconPopIn 0.7s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  opacity: 0;
}

.icon-bg-pulse {
  position: absolute;
  inset: -4px;
  border-radius: 24px;
  background: linear-gradient(135deg, #f46523, #ff8c5a);
  z-index: -1;
  animation: pulse 3s ease-in-out infinite;
}

.kpi-card:hover .icon-area {
  transform: scale(1.08) rotate(-5deg);
  box-shadow:
    0 12px 32px rgba(244, 101, 35, 0.5),
    inset 0 -2px 8px rgba(0, 0, 0, 0.15),
    inset 0 2px 4px rgba(255, 255, 255, 0.3);
}

.icon-area i {
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.text-section {
  text-align: left;
  flex: 1;
  transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.kpi-card:hover .text-section {
  transform: translateX(6px);
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 900;
  color: #0f172a;
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
}

.number-animate {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.05));
}

.plus-sign {
  color: #f46523;
  font-size: 1.75rem;
  font-weight: 800;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: inline-block;
}

.kpi-card:hover .plus-sign {
  transform: rotate(90deg) scale(1.2);
  color: #ff6b3d;
}

.stat-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.fill {
  height: 100%;
  background: linear-gradient(90deg, #f46523 0%, #ff6b3d 50%, #ff8c5a 100%);
  border-radius: 10px;
  transition: width 1.2s cubic-bezier(0.65, 0, 0.35, 1);
  box-shadow:
    0 2px 8px rgba(244, 101, 35, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;
}

.floating-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.kpi-card:hover .floating-particles {
  opacity: 1;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(244, 101, 35, 0.8), transparent);
  animation: float 4s ease-in-out infinite;
  animation-delay: var(--delay);
  left: var(--x);
  top: var(--y);
}

@media (max-width: 768px) {
  .kpi-card {
    padding: 1.5rem;
    border-radius: 1.25rem;
  }

  .content {
    gap: 1rem;
  }

  .icon-area {
    width: 56px;
    height: 56px;
    font-size: 24px;
    border-radius: 16px;
  }

  .stat-value {
    font-size: 1.875rem;
  }

  .plus-sign {
    font-size: 1.375rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .kpi-card:active {
    transform: translateY(-6px) scale(0.98);
  }
}
</style>
